import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ExternalLink, Link2, Phone, Mail, MessageCircle, ChevronDown, Eye, EyeOff, Download, AlertTriangle, Filter, X } from "lucide-react";

const TYPE_ICONS = {
  interno: Link2,
  externo: ExternalLink,
  telefono: Phone,
  mailto: Mail,
  whatsapp: MessageCircle,
  dropdown: ChevronDown,
  otro: Link2,
  vacio: AlertTriangle,
  "interno (con params promo)": Link2,
};

const TYPE_COLORS = {
  interno: "bg-blue-50 text-blue-700 border-blue-200",
  externo: "bg-purple-50 text-purple-700 border-purple-200",
  telefono: "bg-emerald-50 text-emerald-700 border-emerald-200",
  mailto: "bg-amber-50 text-amber-700 border-amber-200",
  whatsapp: "bg-green-50 text-green-700 border-green-200",
  dropdown: "bg-slate-50 text-slate-700 border-slate-200",
  otro: "bg-slate-50 text-slate-600 border-slate-200",
  vacio: "bg-red-50 text-red-700 border-red-200",
  "interno (con params promo)": "bg-orange-50 text-orange-700 border-orange-200",
};

export default function CTAsAuditPage() {
  const { axiosAuth } = useAuth();
  const [data, setData] = useState({ ctas: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSection, setFilterSection] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterVisible, setFilterVisible] = useState("all");
  const [filterHardcoded, setFilterHardcoded] = useState("all");

  useEffect(() => {
    axiosAuth().get("/audit/ctas").then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const sections = useMemo(() => {
    const s = new Set();
    data.ctas.forEach(c => {
      const sec = c.section.split(" > ")[0];
      s.add(sec);
    });
    return Array.from(s).sort();
  }, [data.ctas]);

  const types = useMemo(() => {
    const t = new Set();
    data.ctas.forEach(c => t.add(c.destination_type));
    return Array.from(t).sort();
  }, [data.ctas]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return data.ctas.filter(c => {
      if (q && !c.text.toLowerCase().includes(q) && !c.destination.toLowerCase().includes(q) && !c.section.toLowerCase().includes(q) && !c.editable_from.toLowerCase().includes(q)) return false;
      if (filterSection !== "all" && !c.section.startsWith(filterSection)) return false;
      if (filterType !== "all" && c.destination_type !== filterType) return false;
      if (filterVisible === "visible" && !c.visible) return false;
      if (filterVisible === "oculto" && c.visible) return false;
      if (filterHardcoded === "si" && !c.hardcoded) return false;
      if (filterHardcoded === "no" && c.hardcoded) return false;
      return true;
    });
  }, [data.ctas, search, filterSection, filterType, filterVisible, filterHardcoded]);

  const exportCSV = () => {
    const header = "Seccion;Texto;Destino;Tipo;Visible;Pagina;Editable desde;Orden;Hardcodeado\n";
    const rows = filtered.map(c =>
      `"${c.section}";"${c.text}";"${c.destination}";"${c.destination_type}";"${c.visible ? "Si" : "No"}";"${c.page}";"${c.editable_from}";${c.order};"${c.hardcoded ? "Si" : "No"}"`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ctas_audit_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => { setSearch(""); setFilterSection("all"); setFilterType("all"); setFilterVisible("all"); setFilterHardcoded("all"); };
  const hasFilters = search || filterSection !== "all" || filterType !== "all" || filterVisible !== "all" || filterHardcoded !== "all";
  const hardcodedCount = data.ctas.filter(c => c.hardcoded).length;

  return (
    <div className="space-y-5" data-testid="ctas-audit-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase" data-testid="ctas-audit-title">CTAs y Enlaces</h1>
          <p className="text-sm text-slate-500 mt-0.5">Vista completa de todos los CTAs, enlaces y destinos de la web publica</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} className="rounded-sm text-[10px] uppercase tracking-wider h-8 px-3 flex-shrink-0" data-testid="export-ctas-btn">
          <Download className="w-3 h-3 mr-1" /> Exportar CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-slate-200 rounded-sm"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-slate-900">{data.total}</p><p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Total CTAs</p>
        </CardContent></Card>
        <Card className="border-slate-200 rounded-sm"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{data.ctas.filter(c => c.visible).length}</p><p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Visibles</p>
        </CardContent></Card>
        <Card className="border-slate-200 rounded-sm"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-amber-600">{hardcodedCount}</p><p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Hardcodeados</p>
        </CardContent></Card>
        <Card className="border-slate-200 rounded-sm"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-slate-600">{sections.length}</p><p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Secciones</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por texto, destino, seccion..." className="pl-8 rounded-sm h-8 text-xs border-slate-200" data-testid="ctas-search" />
            </div>
            <Select value={filterSection} onValueChange={setFilterSection}>
              <SelectTrigger className="w-[160px] rounded-sm h-8 text-xs border-slate-200" data-testid="ctas-filter-section"><SelectValue placeholder="Seccion" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las secciones</SelectItem>
                {sections.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px] rounded-sm h-8 text-xs border-slate-200" data-testid="ctas-filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterVisible} onValueChange={setFilterVisible}>
              <SelectTrigger className="w-[120px] rounded-sm h-8 text-xs border-slate-200"><SelectValue placeholder="Visible" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="visible">Visibles</SelectItem>
                <SelectItem value="oculto">Ocultos</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterHardcoded} onValueChange={setFilterHardcoded}>
              <SelectTrigger className="w-[140px] rounded-sm h-8 text-xs border-slate-200"><SelectValue placeholder="Hardcodeado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="si">Hardcodeados</SelectItem>
                <SelectItem value="no">Configurables</SelectItem>
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-2 text-xs text-slate-400">
                <X className="w-3 h-3 mr-1" /> Limpiar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-2 px-4 pt-3">
          <CardDescription className="text-[10px] uppercase tracking-wider font-semibold">{filtered.length} de {data.total} elementos</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-400">Cargando...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="ctas-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left p-2.5 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Seccion</th>
                    <th className="text-left p-2.5 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Texto</th>
                    <th className="text-left p-2.5 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Destino</th>
                    <th className="text-left p-2.5 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Tipo</th>
                    <th className="text-center p-2.5 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Visible</th>
                    <th className="text-left p-2.5 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Pagina</th>
                    <th className="text-left p-2.5 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Editable desde</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c, i) => {
                    const Icon = TYPE_ICONS[c.destination_type] || Link2;
                    const typeColor = TYPE_COLORS[c.destination_type] || TYPE_COLORS.otro;
                    return (
                      <tr key={i} className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${c.hardcoded ? "bg-amber-50/30" : ""}`} data-testid={`cta-row-${i}`}>
                        <td className="p-2.5">
                          <span className="text-[10px] font-medium text-slate-600">{c.section}</span>
                        </td>
                        <td className="p-2.5">
                          <span className="font-medium text-slate-900">{c.text || <span className="text-slate-300 italic">Sin texto</span>}</span>
                        </td>
                        <td className="p-2.5">
                          <code className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">{c.destination || "-"}</code>
                        </td>
                        <td className="p-2.5">
                          <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold border px-1.5 py-0.5 rounded-sm ${typeColor}`}>
                            <Icon className="w-2.5 h-2.5" /> {c.destination_type}
                          </span>
                        </td>
                        <td className="p-2.5 text-center">
                          {c.visible ? <Eye className="w-3.5 h-3.5 text-emerald-500 mx-auto" /> : <EyeOff className="w-3.5 h-3.5 text-slate-300 mx-auto" />}
                        </td>
                        <td className="p-2.5">
                          <span className="text-[10px] text-slate-500">{c.page}</span>
                        </td>
                        <td className="p-2.5">
                          <div className="flex items-center gap-1.5">
                            {c.hardcoded && <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />}
                            <span className={`text-[10px] ${c.hardcoded ? "text-amber-700 font-semibold" : "text-slate-500"}`}>{c.editable_from}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
